'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { getDuration, getMultiplier, multiplyChildren } from '~/utils/marquee';

import styles from './cbaImageColumn.module.scss';
import { type CbaImagesCollection, type HiglightedTag } from './cbaScrollFeature';

type ColumnImage = NonNullable<CbaImagesCollection>['items'][number];

type ShownColumnImage = ColumnImage & {
  newWidth: number;
  newHeight: number;
};

interface CbaImageColumnProps {
  images: ColumnImage[];
  direction: React.CSSProperties['animationDirection'];
  isPaused: boolean;
  highlightedTag?: HiglightedTag;
}

const calculateNewHeight = (
  originalImageHeight: number,
  originalImageWidth: number,
  columnWidth: number,
) => {
  return (columnWidth * originalImageHeight) / originalImageWidth;
};

export const CbaImageColumn = ({
  images,
  direction,
  highlightedTag,
  isPaused,
}: CbaImageColumnProps) => {
  const [colSize, setColSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [imageSetInnerHeight, setImageSetInnerHeight] = useState(0);

  const imageSetInnerRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  const isColSizeSet = colSize.width > 0 || colSize.height > 0;

  useEffect(() => {
    const column = columnRef.current;
    const imageSetInner = imageSetInnerRef.current;

    if (!column || !imageSetInner) return;
    const resizeObserver = new ResizeObserver(() => {
      const columnRect = column.getBoundingClientRect();
      const imageSetInnerRect = imageSetInner.getBoundingClientRect();
      setColSize({ width: columnRect.width, height: columnRect.height });
      setImageSetInnerHeight(imageSetInnerRect.height);
    });
    resizeObserver.observe(column);
    resizeObserver.observe(imageSetInner);
    return () => resizeObserver.disconnect();
  }, []);

  const shownImages: ShownColumnImage[] = [];
  let combinedHeights = 0;

  if (isColSizeSet) {
    const maxCombinedImageHeights = colSize?.height;

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const newHeight = calculateNewHeight(
        image?.height as number,
        image?.width as number,
        colSize?.width,
      );

      if (combinedHeights < maxCombinedImageHeights) {
        combinedHeights += newHeight;

        //@ts-expect-error not important
        const newImage: ShownColumnImage = {
          ...image,
          newWidth: colSize?.width,
          newHeight,
        };
        shownImages.push(newImage);
      } else {
        break;
      }
    }
  }

  const SPEED = 20;

  const multiplier = getMultiplier(imageSetInnerHeight, colSize?.height);
  const duration = getDuration(imageSetInnerHeight, multiplier, SPEED);

  const imageElements = shownImages.map((image, i) => {
    const isHighlighted = image.contentfulMetadata.tags.filter(
      tag => tag?.id === highlightedTag,
    ).length;

    return (
      <div
        className={cx(
          styles.colImageWrapper,
          highlightedTag && !isHighlighted && styles.dimmed,
          isHighlighted && styles.highlighted,
        )}
        key={i}
      >
        <Image
          className={styles.colImage}
          src={image?.url as string}
          alt={image?.title || ''}
          width={image?.newWidth}
          height={image?.newHeight}
        />
      </div>
    );
  });

  const marqueeStyle: React.CSSProperties = {
    animationDirection: direction,
    animationDuration: `${duration}s`,
    animationPlayState: isPaused ? 'paused' : 'running',
    animationDelay: `0s`,
  };

  return (
    <div className={styles.column} ref={columnRef}>
      <div className={styles.imageSet} style={marqueeStyle}>
        <div className={styles.imageSetInner} ref={imageSetInnerRef}>
          {imageElements}
        </div>
        {multiplyChildren(multiplier - 1, imageElements)}
      </div>
      <div className={styles.imageSet} style={marqueeStyle}>
        {multiplyChildren(multiplier, imageElements)}
      </div>
    </div>
  );
};
